import { keepPreviousData, queryOptions } from '@tanstack/react-query'

import { api } from '@/shared/api/http'
import { type BankTransactionOut, type OrderBy, type Page } from '@/shared/api/types'

/** Sentido do fluxo. Espelha o `direction` do processor. */
export type BankTxDirection = 'payable' | 'receivable'

/**
 * Status possíveis de uma transação bancária (enum do processor).
 *
 * As VISÕES em aberto = open+overdue+partial; as visões pagas/recebidas =
 * settled. `unknown` aparece quando o fornecedor manda um status que ainda não
 * mapeamos.
 */
export type BankTxStatus = 'open' | 'settled' | 'canceled' | 'overdue' | 'partial' | 'unknown'

/** `order_by` aceitos pelo processor (docstring do endpoint). */
export type BankTxOrderBy = OrderBy<'due_date' | 'amount' | 'status' | 'created_at'>

/** Status em aberto (as telas "a pagar"/"a receber"). */
export const OPEN_STATUSES: readonly BankTxStatus[] = ['open', 'overdue', 'partial']
/** Status baixado (as telas "pagas"/"recebidas"). */
export const SETTLED_STATUSES: readonly BankTxStatus[] = ['settled']
/**
 * Tudo que a UI mostra: todos os status MENOS `canceled`. Cancelado sumiu da
 * interface — o back ainda pode devolvê-lo, então o TIPO mantém 'canceled' e o
 * extrato pede EXPLICITAMENTE o resto (a query nunca traz cancelado).
 */
export const VISIBLE_STATUSES: readonly BankTxStatus[] = [
  'open',
  'settled',
  'overdue',
  'partial',
  'unknown',
]

/** Params aceitos por `GET /v1/bank-transactions` (a fronteira do request). */
export interface BankTxListParams {
  readonly direction?: BankTxDirection
  /** REPETÍVEL: vira `?status=a&status=b`. */
  readonly status?: readonly BankTxStatus[]
  readonly integration_id?: string
  readonly group_id?: string
  readonly due_after?: string
  readonly due_before?: string
  readonly order_by?: BankTxOrderBy
  readonly limit?: number
  readonly offset?: number
}

export const bankTransactionKeys = {
  all: () => ['bank-transactions'] as const,
  lists: () => [...bankTransactionKeys.all(), 'list'] as const,
  list: (p: BankTxListParams) => [...bankTransactionKeys.lists(), p] as const,
} as const

/**
 * GET /v1/bank-transactions — envelope `Page` do processor, repassado pelo
 * back-end. Dinheiro (`amount`/`settled_amount`) já vem como string decimal.
 */
export const bankTransactionsQueryOptions = (p: BankTxListParams) =>
  queryOptions({
    queryKey: bankTransactionKeys.list(p),
    queryFn: ({ signal }) =>
      api.get<Page<BankTransactionOut>>('/v1/bank-transactions', { ...p }, signal),
    // Mudar filtro/página mantém as linhas antigas em vez de piscar (envelope
    // traz `total`, então a paginação não salta).
    placeholderData: keepPreviousData,
  })
